import {
  Box,
  Button,
  Flex,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Textarea,
  useToast,
} from '@chakra-ui/react';
import { FormEvent, useEffect, useRef, useState, ChangeEvent, DragEvent } from 'react';
import ResizeTextarea from 'react-textarea-autosize';
import {
  ATTACHMENT_MAX_FILE_SIZE,
  UPLOADED_ATTACHMENT_MAX_TOTAL_SIZE_PER_MESSAGE,
  MAX_TOTAL_ATTACHMENTS_SIZE_PER_CONVERSATION,
  MAX_TOTAL_ATTACHMENT_TOKENS_PER_CONVERSATION,
  UPLOADED_ATTACHMENT_MAX_FILES_PER_MESSAGE,
  buildFilePickerAcceptString,
  isSupportedMimeType,
  SUPPORTED_EXTENSIONS,
} from '@worknet/models';

import useChatStore from '~/shared/utils/useChatStore';
import BouncingLoader from '~/shared/components/ds/BouncingLoader.tsx';

import { FilePreview } from './FilePreview';
import SendSvg from './assets/Send.svg?react';
import AttachmentIcon from './assets/attachment.svg?react';

const DEFAULT_HEIGHT = 39;
const DEFAULT_PLACE_HOLDER = 'Type a message...';

const ACCEPT_ATTRIBUTE = buildFilePickerAcceptString();

const formatMB = (bytes: number) => `${Math.round(bytes / (1024 * 1024))}MB`;

function validateFile(file: File): { valid: boolean; error?: string } {
  if (file.size > ATTACHMENT_MAX_FILE_SIZE) {
    return {
      valid: false,
      error: `File "${file.name}" exceeds the maximum size of ${formatMB(ATTACHMENT_MAX_FILE_SIZE)}`,
    };
  }

  const isAllowedType = isSupportedMimeType(file.type);
  const ext = file.name.toLowerCase().match(/\.(\w+)$/)?.[1];
  const hasAllowedExtension = ext ? SUPPORTED_EXTENSIONS.has(ext) : false;

  if (!isAllowedType && !hasAllowedExtension) {
    return {
      valid: false,
      error: `File "${file.name}" has an unsupported format. Allowed: images, PDF, and text-based files`,
    };
  }

  return { valid: true };
}

function validateTotalSize(files: File[], newFiles: File[]): { valid: boolean; error?: string } {
  const totalSize = [...files, ...newFiles].reduce((sum, file) => sum + file.size, 0);

  if (totalSize > UPLOADED_ATTACHMENT_MAX_TOTAL_SIZE_PER_MESSAGE) {
    return {
      valid: false,
      error: `Total file size exceeds the maximum limit of ${formatMB(UPLOADED_ATTACHMENT_MAX_TOTAL_SIZE_PER_MESSAGE)}`,
    };
  }

  return { valid: true };
}

export default function UserInput({
  onSubmit,
  isLoading,
  files: externalFiles = [],
  onClearFiles,
  allowFileUpload = true,
  conversationAttachmentSize,
  conversationAttachmentTokens,
  pendingText,
}: {
  isLoading?: boolean;
  files?: File[];
  onClearFiles?: () => void;
  onSubmit: (text: string, files?: File[]) => void;
  allowFileUpload?: boolean;
  conversationAttachmentSize?: number;
  conversationAttachmentTokens?: number;
  pendingText?: string;
}) {
  const [text, setText] = useState('');
  const [isMultiLine, setIsMultiLine] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    if (externalFiles.length > 0) {
      addFilesWithValidation(externalFiles);
      if (onClearFiles) {
        onClearFiles();
      }
    }
  }, [externalFiles]);

  useEffect(() => {
    if (pendingText) {
      setText(pendingText);
      useChatStore.setState({ pendingText: undefined });
    }
  }, [pendingText]);

  const inputRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const disabled = !text.trim() && !files.length;
  const toast = useToast();

  const addFilesWithValidation = (newFiles: File[]) => {
    if (files.length >= UPLOADED_ATTACHMENT_MAX_FILES_PER_MESSAGE) {
      toast({
        description: 'You can add up to 3 attachments per message',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    const availableSlots = UPLOADED_ATTACHMENT_MAX_FILES_PER_MESSAGE - files.length;
    const filesToProcess = newFiles.slice(0, availableSlots);
    const filesRejectedDueToLimit = newFiles.length > availableSlots;

    const validatedFiles: File[] = [];

    for (const file of filesToProcess) {
      const validation = validateFile(file);
      if (!validation.valid) {
        toast({
          title: 'File Validation Error',
          description: validation.error,
          duration: 5000,
          isClosable: true,
        });
        continue;
      }
      validatedFiles.push(file);
    }

    if (validatedFiles.length === 0) {
      return;
    }

    const totalSizeValidation = validateTotalSize(files, validatedFiles);
    if (!totalSizeValidation.valid) {
      toast({
        title: 'File Size Error',
        description: totalSizeValidation.error,
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    const newFilesSize = [...files, ...validatedFiles].reduce((sum, f) => sum + f.size, 0);
    if (
      (conversationAttachmentSize ?? 0) + newFilesSize >
      MAX_TOTAL_ATTACHMENTS_SIZE_PER_CONVERSATION
    ) {
      toast({
        title: 'File Size Error',
        description: `Total attachments in this conversation exceed the ${formatMB(MAX_TOTAL_ATTACHMENTS_SIZE_PER_CONVERSATION)} limit`,
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    if (
      conversationAttachmentTokens !== undefined &&
      conversationAttachmentTokens >= MAX_TOTAL_ATTACHMENT_TOKENS_PER_CONVERSATION
    ) {
      toast({
        title: 'Attachment Limit Reached',
        description: 'This conversation has reached the maximum attachment token limit',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    setFiles([...files, ...validatedFiles]);
    if (filesRejectedDueToLimit) {
      toast({
        description: 'You can add up to 3 attachments per message',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      const newFiles = Array.from(event.target.files);
      addFilesWithValidation(newFiles);
      // Reset input value to allow selecting the same file again
      event.target.value = '';
    }
  };

  const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);

    if (event.dataTransfer.files) {
      const newFiles = Array.from(event.dataTransfer.files);
      addFilesWithValidation(newFiles);
    }
  };

  const removeFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (isLoading) {
      return;
    }
    if (disabled) {
      toast({
        title: 'Error',
        status: 'error',
        description: 'Please enter text before submitting',
        isClosable: true,
      });
      return;
    }
    onSubmit(text, files);
    setText('');
    setFiles([]);
    if (onClearFiles) {
      onClearFiles();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  useEffect(() => {
    const inputField = inputRef.current;

    const isNonUserInput = (e: KeyboardEvent) => {
      // ignore shortcuts like Ctrl+C, Ctrl+V and function keys, arrows, Escape, Tab etc.
      return e.ctrlKey || e.metaKey || e.altKey || e.key.length !== 1;
    };

    const handleAutoFocusOnKeyDown = (e: KeyboardEvent) => {
      if (!inputField || document.activeElement === inputField || isNonUserInput(e)) {
        return;
      }

      inputField.focus();
    };

    document.addEventListener('keydown', handleAutoFocusOnKeyDown);
    return () => {
      document.removeEventListener('keydown', handleAutoFocusOnKeyDown);
    };
  }, []);

  return (
    <form onSubmit={handleSubmit} onKeyDown={handleKeyDown}>
      {files.length > 0 && (
        <Flex gap={2} minH="66px" h="fit-content" overflowX="auto" overflowY="hidden" pt="8px">
          {files.map((file, index) => (
            <FilePreview
              key={`${file.name}-${index}`}
              file={file}
              onRemove={() => removeFile(index)}
            />
          ))}
        </Flex>
      )}
      <Input
        type="file"
        multiple
        accept={ACCEPT_ATTRIBUTE}
        ref={fileInputRef}
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />

      <InputGroup
        onDragOver={handleDragOver}
        onDragEnter={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        border={isDragging ? '1px dashed' : undefined}
        borderColor={isDragging ? 'blue.500' : undefined}
        background={isDragging ? 'blue.50' : undefined}
        borderRadius={5}
        transition="all 0.2s"
      >
        {allowFileUpload && (
          <InputLeftElement h="full" border="none">
            <Box h="full" w="fit" display="flex" alignItems="center">
              <Icon
                boxSize={6}
                stroke="greyscale.950"
                cursor="pointer"
                onClick={() => fileInputRef.current?.click()}
              >
                <AttachmentIcon />
              </Icon>
            </Box>
          </InputLeftElement>
        )}

        <Textarea
          ref={inputRef}
          as={ResizeTextarea}
          onHeightChange={(h: number) => setIsMultiLine(h !== DEFAULT_HEIGHT)}
          rows={1}
          maxRows={6}
          _focus={{ outlineColor: 'transparent', boxShadow: '0px 0px 7px 0px #587AFF' }}
          onFocus={(e) => (e.target.placeholder = 'Typing')}
          onBlur={(e) => (e.target.placeholder = DEFAULT_PLACE_HOLDER)}
          placeholder={DEFAULT_PLACE_HOLDER}
          fontSize="chatText"
          borderRadius="5px"
          border="none"
          boxShadow="0 0 0 1px var(--chakra-colors-greyNatural)"
          _placeholder={{ color: 'greyDark' }}
          color="black"
          lineHeight="23px"
          value={text}
          resize="none"
          css={{ '&::-webkit-scrollbar': { width: '0px' } }}
          pr={isMultiLine ? '50px' : '40px'}
          pl={allowFileUpload ? '30px' : undefined}
          onChange={(e) => setText(e.target?.value)}
        />
        <InputRightElement h="full" border="none">
          <Button
            h="full"
            fontSize="chatText"
            borderLeftRadius="0px"
            borderRightRadius="4px"
            type="submit"
            variant="link"
            disabled={disabled}
            pointerEvents={isLoading ? 'none' : 'auto'}
            backgroundColor={'main'}
            zIndex={5}
            {...(isMultiLine
              ? {
                  height: '27px',
                  minW: '30px',
                  mt: 'auto',
                  mr: '12px',
                  mb: '8px',
                  borderRadius: '3px',
                  overflow: 'hidden',
                }
              : {})}
          >
            {isLoading ? (
              <BouncingLoader variant="btn" />
            ) : (
              <Icon as={SendSvg} boxSize={isMultiLine ? '12px' : '16px'} ml="1px" fill="tertiary" />
            )}
          </Button>
        </InputRightElement>
      </InputGroup>
    </form>
  );
}
